import { useState, useRef, useEffect } from 'react';
import React from 'react';
import classes from './SearchableSelect.module.css';

type SearchableSelectProps = {
  name?: string;
  children: React.ReactNode;
  defaultValue?: string | number;
  value?: string | number;
  placeholderText?: string;
  onChange?: (value: string | number) => void;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

type ExtractedOption = {
  value: string;
  label: string;
  group: string | null;
};

function SearchableSelect({
  name,
  children,
  defaultValue = '',
  placeholderText = 'Select...',
  value,
  onChange,
  ...props
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [options, setOptions] = useState<ExtractedOption[]>([]);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const selected = value !== undefined ? value : internalValue;

  //after the select mounts, extract options from the hidden select element in the DOM
  useEffect(() => {
    if (selectRef.current) {
      const extractedOptions: ExtractedOption[] = [];

      const selectElement = selectRef.current;
      for (let i = 0; i < selectElement.options.length; i++) {
        const option = selectElement.options[i];
        const optgroup =
          option.parentElement?.tagName === 'OPTGROUP'
            ? (option.parentElement as HTMLOptGroupElement).label
            : null;

        extractedOptions.push({
          value: option.value,
          label: option.text,
          group: optgroup,
        });
      }

      setOptions(extractedOptions);
    }
  }, [children]);

  const filtered = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (selectedValue: string | number) => {
    // Only update internal state if component is uncontrolled
    if (value === undefined) {
      setInternalValue(selectedValue);
    }

    if (selectRef.current) {
      selectRef.current.value = String(selectedValue);
    }

    if (onChange) onChange(selectedValue);

    setIsOpen(false);
    setSearch('');
  };

  const selectedLabel =
    options.find(o => o.value === String(selected))?.label || placeholderText;

  return (
    <div className={classes.container}>
      {value === undefined ? (
        //hidden uncontrolled select
        <select
          ref={selectRef}
          name={name}
          defaultValue={defaultValue}
          onChange={e => onChange?.(e.target.value)}
          className={classes.hiddenSelect}
          {...props}
        >
          {children}
        </select>
      ) : (
        //hidden controlled select
        <select
          ref={selectRef}
          name={name}
          value={value ?? ''}
          onChange={e => onChange?.(e.target.value)}
          className={classes.hiddenSelect}
          {...props}
        >
          {children}
        </select>
      )}

      <div className={classes.inputWrapper}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          placeholder={selectedLabel}
          className={classes.input}
        />

        {isOpen && (
          <div className={classes.dropdown}>
            {filtered.map(opt => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={classes.option}
              >
                <span>{opt.label}</span>
                {opt.group && (
                  <span className={classes.optionGroup}>{opt.group}</span>
                )}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className={classes.noResults}>No results found.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchableSelect;
